import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { GoogleMap, Marker, Polyline } from '@react-google-maps/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  MapPin, Clock, Camera, Lock, Send, Image as ImageIcon, Navigation,
  Radio, CheckCircle2, X, Users, MessageCircle, ChevronDown, ChevronUp,
  ExternalLink, Briefcase, LogOut, Mail, FileText, RefreshCw,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const SESSION_KEY_PREFIX = 'photoplan:share-session:';
const SHARE_EMAIL_KEY = 'photoplan:share-email:';
const PLAN_POLL_MS = 8000;
const MSG_POLL_MS = 5000;
const LOC_POLL_MS = 10000;
const TRACK_PUSH_MS = 30000;

const photographerColors = [
  '#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899',
  '#14b8a6', '#f97316', '#6366f1', '#06b6d4',
];

function colorForPhotographer(id, list) {
  if (!id || !list) return photographerColors[0];
  const idx = list.findIndex((p) => p.id === id);
  return photographerColors[(idx >= 0 ? idx : 0) % photographerColors.length];
}

function fmtTime(t) {
  if (!t) return '';
  const [h, m] = String(t).split(':');
  const hour = parseInt(h, 10);
  if (Number.isNaN(hour)) return t;
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m} ${ampm}`;
}

function shortTime(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function buildAuthHeader(session) {
  return session ? { 'X-Share-Session': session } : {};
}

function mapsUrl(spot) {
  if (spot?.lat != null && spot?.lng != null) {
    return `https://www.google.com/maps/search/?api=1&query=${spot.lat},${spot.lng}`;
  }
  if (spot?.address) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(spot.address)}`;
  }
  return null;
}

export default function PlanSharePublic() {
  const { token } = useParams();
  const { t } = useTranslation();

  // Session lifecycle
  const [info, setInfo] = useState(null);
  const [infoErr, setInfoErr] = useState(null);
  const [session, setSession] = useState(() => {
    try { return sessionStorage.getItem(SESSION_KEY_PREFIX + token) || null; } catch { return null; }
  });
  const [email, setEmail] = useState(() => {
    try { return localStorage.getItem(SHARE_EMAIL_KEY + token) || ''; } catch { return ''; }
  });
  const [password, setPassword] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [authErr, setAuthErr] = useState(null);

  // Live data
  const [planData, setPlanData] = useState(null);
  const [planErr, setPlanErr] = useState(null);
  const [messages, setMessages] = useState([]);
  const [tracking, setTracking] = useState({ live: [], arrivals: [] });

  // Chat composer
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [chatOpen, setChatOpen] = useState(false); // collapsed by default
  const fileInputRef = useRef(null);

  // Tracking — passive ping + spot-location update are always on.
  // Spot view filter — default to "my spots only" with toggle to see team's.
  const [busySpot, setBusySpot] = useState(null); // spotId currently doing checkin/checkout
  const [showAllSpots, setShowAllSpots] = useState(false);

  // UI
  const [expandedSpot, setExpandedSpot] = useState(null);
  const [activeTab, setActiveTab] = useState('plan'); // plan | map (mobile)
  const [changedSpots, setChangedSpots] = useState([]); // {id, name} list — recently updated, dismissable
  const spotsSnapshotRef = useRef(null); // {id: updated_at}
  const messagesScrollerRef = useRef(null);

  /* -------------------- Fetch info -------------------- */
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const r = await axios.get(`${API}/share/${token}/info`);
        if (!cancelled) setInfo(r.data);
      } catch (err) {
        if (cancelled) return;
        const status = err.response?.status;
        if (status === 410) setInfoErr(t('planShare.expired'));
        else setInfoErr(t('planShare.linkUnavailable'));
      }
    };
    run();
    return () => { cancelled = true; };
  }, [token, t]);

  const handleUnlock = async (e) => {
    e.preventDefault();
    if (info?.has_password && !password.trim()) {
      setAuthErr(t('planShare.passwordRequired'));
      return;
    }
    if (!email.trim()) {
      setAuthErr(t('planShare.emailRequired'));
      return;
    }
    setAuthBusy(true);
    setAuthErr(null);
    try {
      const r = await axios.post(`${API}/share/${token}/auth`, {
        password: info?.has_password ? password : null,
        email: email.trim(),
      });
      sessionStorage.setItem(SESSION_KEY_PREFIX + token, r.data.session);
      try { localStorage.setItem(SHARE_EMAIL_KEY + token, email.trim()); } catch { /* noop */ }
      setSession(r.data.session);
      setPassword('');
      if (!r.data.photographer_id) {
        toast.warning(t('planShare.emailNotFound'));
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) setAuthErr(t('planShare.wrongPassword'));
      else setAuthErr(err.response?.data?.detail || t('planShare.linkUnavailable'));
    } finally {
      setAuthBusy(false);
    }
  };

  const handleSignOut = () => {
    sessionStorage.removeItem(SESSION_KEY_PREFIX + token);
    setSession(null);
    setPlanData(null);
  };

  /* -------------------- Polling: plan -------------------- */
  const fetchPlan = useCallback(async () => {
    if (!session) return;
    try {
      const r = await axios.get(`${API}/share/${token}/plan`, {
        headers: buildAuthHeader(session),
      });
      // Detect spot changes vs. last snapshot. Skip on the very first load
      // (don't flag everything as "changed" when the user just opened the link).
      const incomingSpots = r.data?.spots || [];
      if (spotsSnapshotRef.current) {
        const newlyChanged = [];
        for (const s of incomingSpots) {
          const previous = spotsSnapshotRef.current[s.id];
          const current = s.updated_at || s.created_at;
          if (previous && current && current !== previous) {
            newlyChanged.push({ id: s.id, name: s.name });
          } else if (!previous) {
            // Brand new spot added since the last poll
            newlyChanged.push({ id: s.id, name: s.name, isNew: true });
          }
        }
        if (newlyChanged.length) {
          setChangedSpots((prev) => {
            const ids = new Set(prev.map((p) => p.id));
            const next = [...prev];
            for (const c of newlyChanged) {
              if (!ids.has(c.id)) next.push(c);
            }
            return next.slice(-10); // cap
          });
        }
      }
      const snap = {};
      incomingSpots.forEach((s) => { snap[s.id] = s.updated_at || s.created_at || ''; });
      spotsSnapshotRef.current = snap;

      setPlanData(r.data);
      setPlanErr(null);
    } catch (err) {
      const status = err.response?.status;
      if (status === 401) {
        sessionStorage.removeItem(SESSION_KEY_PREFIX + token);
        setSession(null);
      } else if (status === 410) {
        setPlanErr(t('planShare.expired'));
      } else if (!planData) {
        setPlanErr(t('planShare.linkUnavailable'));
      }
    }
  }, [session, token, t, planData]);

  useEffect(() => {
    if (!session) return;
    fetchPlan();
    const id = setInterval(fetchPlan, PLAN_POLL_MS);
    return () => clearInterval(id);
  }, [session, fetchPlan]);

  /* -------------------- Polling: messages -------------------- */
  const lastMsgTsRef = useRef(null);
  const fetchMessages = useCallback(async () => {
    if (!session || planData?.allow_chat === false) return;
    try {
      const params = lastMsgTsRef.current ? { since: lastMsgTsRef.current } : {};
      const r = await axios.get(`${API}/share/${token}/messages`, {
        headers: buildAuthHeader(session),
        params,
      });
      const newMsgs = r.data || [];
      if (newMsgs.length) {
        setMessages((prev) => [...prev, ...newMsgs]);
        lastMsgTsRef.current = newMsgs[newMsgs.length - 1].created_at;
      }
    } catch {
      /* swallow during polling */
    }
  }, [session, token, planData]);

  useEffect(() => {
    if (!session) return;
    fetchMessages();
    const id = setInterval(fetchMessages, MSG_POLL_MS);
    return () => clearInterval(id);
  }, [session, fetchMessages]);

  useEffect(() => {
    if (chatOpen && messagesScrollerRef.current) {
      messagesScrollerRef.current.scrollTop = messagesScrollerRef.current.scrollHeight;
    }
  }, [messages, chatOpen]);

  /* -------------------- Polling: locations -------------------- */
  const fetchTracking = useCallback(async () => {
    if (!session || planData?.allow_tracking === false) return;
    try {
      const r = await axios.get(`${API}/share/${token}/locations`, {
        headers: buildAuthHeader(session),
      });
      setTracking(r.data || { live: [], arrivals: [] });
    } catch {
      /* swallow */
    }
  }, [session, token, planData]);

  useEffect(() => {
    if (!session) return;
    fetchTracking();
    const id = setInterval(fetchTracking, LOC_POLL_MS);
    return () => clearInterval(id);
  }, [session, fetchTracking]);

  /* -------------------- Send message -------------------- */
  const sendMessage = async (overrides = {}) => {
    const body = {
      text: overrides.text !== undefined ? overrides.text : text.trim(),
      image_url: overrides.image_url || null,
      location: overrides.location || null,
    };
    if (!body.text && !body.image_url && !body.location) return;
    setSending(true);
    try {
      const r = await axios.post(`${API}/share/${token}/messages`, body, {
        headers: buildAuthHeader(session),
      });
      setMessages((prev) => [...prev, r.data]);
      lastMsgTsRef.current = r.data.created_at;
      if (overrides.text === undefined) setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.uploadFailed'));
    } finally {
      setSending(false);
    }
  };

  const onSendForm = (e) => { e.preventDefault(); sendMessage(); };
  const handleAttachClick = () => { if (fileInputRef.current) fileInputRef.current.click(); };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('planShare.imageTooLarge'));
      e.target.value = '';
      return;
    }
    const form = new FormData();
    form.append('file', file);
    try {
      const up = await axios.post(`${API}/share/${token}/upload-image`, form, {
        headers: { ...buildAuthHeader(session), 'Content-Type': 'multipart/form-data' },
      });
      await sendMessage({ image_url: up.data.url, text: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.uploadFailed'));
    } finally {
      e.target.value = '';
    }
  };

  const requestPosition = () =>
    new Promise((resolve, reject) => {
      if (!('geolocation' in navigator)) {
        reject(new Error('unsupported'));
        return;
      }
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve(pos),
        (err) => reject(err),
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
      );
    });

  const handleShareLocation = async () => {
    try {
      const pos = await requestPosition();
      const { latitude, longitude } = pos.coords;
      await sendMessage({
        text: '',
        location: { lat: latitude, lng: longitude, label: t('planShare.shareLocation') },
      });
      toast.success(t('planShare.locationShared'));
    } catch (err) {
      if (err?.message === 'unsupported') toast.error(t('planShare.geoUnsupported'));
      else if (err?.code === 1) toast.error(t('planShare.geoDenied'));
      else toast.error(t('planShare.geoFailed'));
    }
  };

  /* -------------------- Tracking pings -------------------- */
  const viewerPid = planData?.viewer_photographer_id;
  const viewerName = planData?.viewer_photographer_name;

  // Passive ping always runs once a viewer has been identified and tracking
  // is allowed by the link — no UI toggle.
  const pushPassivePing = useCallback(async () => {
    if (!session || !viewerPid || planData?.allow_tracking === false) return;
    try {
      const pos = await requestPosition();
      await axios.post(
        `${API}/share/${token}/locations`,
        {
          photographer_id: viewerPid,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          mode: 'passive',
        },
        { headers: buildAuthHeader(session) },
      );
    } catch {
      /* silent */
    }
  }, [session, token, viewerPid, planData]);

  useEffect(() => {
    if (!session || !viewerPid || planData?.allow_tracking === false) return;
    pushPassivePing();
    const id = setInterval(pushPassivePing, TRACK_PUSH_MS);
    return () => clearInterval(id);
  }, [session, viewerPid, planData, pushPassivePing]);

  /* -------------------- Per-spot check in / out -------------------- */
  const checkInSpot = async (spot) => {
    if (!viewerPid) {
      toast.error(t('planShare.identifyFirst'));
      return;
    }
    setBusySpot(spot.id);
    try {
      const pos = await requestPosition();
      await axios.post(
        `${API}/share/${token}/checkin`,
        {
          spot_id: spot.id,
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy_m: pos.coords.accuracy,
          update_spot_location: true,
        },
        { headers: buildAuthHeader(session) },
      );
      toast.success(t('planShare.checkedIn', { name: spot.name }));
      fetchPlan();
      fetchTracking();
    } catch (err) {
      if (err?.message === 'unsupported') toast.error(t('planShare.geoUnsupported'));
      else if (err?.code === 1) toast.error(t('planShare.geoDenied'));
      else toast.error(err.response?.data?.detail || t('planShare.geoFailed'));
    } finally {
      setBusySpot(null);
    }
  };

  const checkOutSpot = async (spot) => {
    if (!viewerPid) return;
    setBusySpot(spot.id);
    try {
      let coords = {};
      try {
        const pos = await requestPosition();
        coords = { lat: pos.coords.latitude, lng: pos.coords.longitude };
      } catch { /* coords optional on checkout */ }
      await axios.post(
        `${API}/share/${token}/checkout`,
        { spot_id: spot.id, ...coords },
        { headers: buildAuthHeader(session) },
      );
      toast.success(t('planShare.checkedOut', { name: spot.name }));
      fetchPlan();
      fetchTracking();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.linkUnavailable'));
    } finally {
      setBusySpot(null);
    }
  };

  /* -------------------- Derived data -------------------- */
  const spots = planData?.spots || [];
  const photographers = planData?.photographers || [];
  const courseTracks = planData?.course_tracks || [];
  const arrivals = planData?.arrivals || tracking.arrivals || [];

  // Active checkin (no departed_at) for the current viewer at a spot
  const arrivalForSpot = (spotId) =>
    arrivals.find((a) => a.spot_id === spotId && a.photographer_id === viewerPid && !a.departed_at);

  // Completed checkin (has departed_at) — used to mark a spot as done
  const completedAtSpot = (spotId) =>
    arrivals.find((a) => a.spot_id === spotId && a.photographer_id === viewerPid && a.departed_at);

  // Identify the "next up" spot for the viewer: assigned, not completed, not
  // currently checked in. Sorted by arrive_time ascending.
  const myAssignedSpotsSortedRef = useMemo(() => {
    if (!viewerPid) return [];
    return spots
      .filter((s) => (s.assigned_photographers || []).includes(viewerPid))
      .sort((a, b) => (a.arrive_time || '').localeCompare(b.arrive_time || ''));
  }, [spots, viewerPid]);

  const nextSpotId = useMemo(() => {
    for (const s of myAssignedSpotsSortedRef) {
      if (completedAtSpot(s.id)) continue;
      if (arrivalForSpot(s.id)) continue; // already checked in here = current
      return s.id;
    }
    return null;
  }, [myAssignedSpotsSortedRef, arrivals, viewerPid]); // eslint-disable-line

  const spotsWithCoords = useMemo(
    () => spots.filter((s) => s.lat != null && s.lng != null),
    [spots],
  );

  const mapCenter = useMemo(() => {
    if (spotsWithCoords.length > 0) {
      return {
        lat: spotsWithCoords.reduce((s, x) => s + x.lat, 0) / spotsWithCoords.length,
        lng: spotsWithCoords.reduce((s, x) => s + x.lng, 0) / spotsWithCoords.length,
      };
    }
    if (tracking.live[0]) return { lat: tracking.live[0].lat, lng: tracking.live[0].lng };
    return { lat: -23.55, lng: -46.63 };
  }, [spotsWithCoords, tracking.live]);

  /* -------------------- Render: error / unlock -------------------- */
  if (infoErr) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-red-500" />
            </div>
            <h2 className="text-lg font-semibold text-slate-800 mb-2">{t('planShare.linkUnavailable')}</h2>
            <p className="text-slate-500 text-sm">{infoErr}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!info) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6">
            <div className="text-center mb-5">
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Camera className="w-8 h-8 text-emerald-600" />
              </div>
              <h1 className="text-xl font-bold text-slate-900">{info.event_name || t('planShare.title')}</h1>
              {info.plan_name && <p className="text-sm text-slate-500 mt-1">{info.plan_name}</p>}
            </div>
            <form onSubmit={handleUnlock} className="space-y-3">
              <div>
                <Label className="text-xs text-slate-600">{t('planShare.emailLabel')}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  autoComplete="email"
                  autoFocus
                />
                <p className="text-xs text-slate-500 mt-1">{t('planShare.emailHint')}</p>
              </div>
              {info.has_password && (
                <div>
                  <Label className="text-xs text-slate-600">{t('planShare.password')}</Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('planShare.password')}
                  />
                </div>
              )}
              {authErr && <p className="text-sm text-red-600">{authErr}</p>}
              <Button type="submit" disabled={authBusy} className="w-full bg-emerald-600 hover:bg-emerald-700">
                {authBusy ? t('planShare.unlocking') : t('planShare.openPlan')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (planErr && !planData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <Lock className="w-8 h-8 text-red-500 mx-auto mb-3" />
            <p className="text-slate-700">{planErr}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!planData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500" />
      </div>
    );
  }

  /* -------------------- Render: main -------------------- */
  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-4 py-3 flex flex-wrap items-center justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold text-slate-900 flex items-center gap-2">
              <span className="truncate">{planData.event?.name || planData.plan?.name}</span>
              <Badge variant="secondary" className="text-[10px] uppercase tracking-wide flex-shrink-0">
                {t('planShare.livePlan')}
              </Badge>
            </h1>
            <p className="text-xs text-slate-500 flex flex-wrap items-center gap-1.5">
              <span>{t('planShare.lastUpdated')}: {shortTime(planData.server_time)}</span>
              {viewerName ? (
                <span className="text-emerald-600">· {viewerName}</span>
              ) : (
                <span className="text-amber-600">· {t('planShare.anonymousViewer')}</span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {/* Mobile tab switcher */}
            <div className="flex md:hidden gap-1">
              {[
                { key: 'plan', label: t('planShare.spots'), icon: MapPin },
                { key: 'map', label: t('planShare.map'), icon: Navigation },
              ].map(({ key, label, icon: Icon }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`px-2 py-1 text-xs rounded-md flex items-center gap-1 ${
                    activeTab === key ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-600'
                  }`}
                >
                  <Icon className="w-3.5 h-3.5" />
                  {label}
                </button>
              ))}
            </div>
            <Button size="sm" variant="ghost" onClick={handleSignOut} title={t('planShare.signOut')}>
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-screen-2xl w-full mx-auto px-2 sm:px-4 py-4 grid md:grid-cols-12 gap-4 pb-24">
        {/* Plan column */}
        <div className={`md:col-span-7 ${activeTab === 'plan' ? 'block' : 'hidden md:block'}`}>
          {changedSpots.length > 0 && (
            <div className="mb-3 bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
              <Radio className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5 animate-pulse" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-blue-900">{t('planShare.spotsUpdated')}</p>
                <p className="text-xs text-blue-700 mt-0.5">
                  {changedSpots.map((c) => c.name).join(', ')}
                </p>
              </div>
              <Button
                type="button"
                size="sm"
                variant="ghost"
                onClick={() => setChangedSpots([])}
                className="h-7 px-2 text-blue-700 hover:bg-blue-100"
              >
                <X className="w-3.5 h-3.5" />
              </Button>
            </div>
          )}

          {/* Plan description / instructions — shown above the spots list so
              the photographer reads it first when the page loads. */}
          {(planData.plan?.description_html || planData.plan?.event_description || planData.plan?.instructions) && (
            <Card className="mb-3">
              <CardContent className="p-3">
                <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  {t('planShare.planDescription', { defaultValue: 'Plan description' })}
                </h2>
                {planData.plan?.description_html ? (
                  <div
                    className="prose prose-sm max-w-none text-sm text-slate-700"
                    dangerouslySetInnerHTML={{ __html: planData.plan.description_html }}
                  />
                ) : (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">
                    {planData.plan?.event_description || planData.plan?.instructions}
                  </p>
                )}
                {planData.plan?.instructions && planData.plan?.description_html && (
                  <p className="text-sm text-slate-700 whitespace-pre-wrap mt-2 pt-2 border-t border-slate-100">
                    {planData.plan.instructions}
                  </p>
                )}
              </CardContent>
            </Card>
          )}

          {viewerPid && (
            <Card className="mb-3">
              <CardContent className="p-3 flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 text-xs">
                  <button
                    type="button"
                    onClick={() => setShowAllSpots(false)}
                    className={`px-2.5 py-1 rounded-full border text-xs ${
                      !showAllSpots ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-200'
                    }`}
                  >
                    {t('planShare.mySpotsOnly')}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAllSpots(true)}
                    className={`px-2.5 py-1 rounded-full border text-xs ${
                      showAllSpots ? 'bg-emerald-600 text-white border-emerald-700' : 'bg-white text-slate-700 border-slate-200'
                    }`}
                  >
                    {t('planShare.allSpots')}
                  </button>
                </div>
                <span className="ml-auto text-xs text-slate-500 flex items-center gap-1">
                  <Users className="w-3 h-3" /> {tracking.live.length} {t('planShare.online')}
                </span>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-3 sm:p-4">
              {(() => {
                const visibleSpots = (viewerPid && !showAllSpots)
                  ? spots.filter((s) => (s.assigned_photographers || []).includes(viewerPid))
                  : spots;
                return (
                  <>
                    <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-3">
                      {t('planShare.spots')} ({visibleSpots.length}{visibleSpots.length !== spots.length ? `/${spots.length}` : ''})
                    </h2>
                    {visibleSpots.length === 0 ? (
                      <p className="text-sm text-slate-400 py-6 text-center">
                        {spots.length === 0 ? '—' : t('planShare.noMySpots')}
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {[...visibleSpots]
                          .sort((a, b) => (a.arrive_time || '').localeCompare(b.arrive_time || ''))
                          .map((s, i) => {
                      const arrival = arrivalForSpot(s.id);
                      const completed = completedAtSpot(s.id);
                      const isNext = !completed && !arrival && s.id === nextSpotId;
                      // Auto-collapse completed spots; they shrink and dim,
                      // user can click to re-expand to retry/re-checkin.
                      const isExpanded = expandedSpot === s.id || (!completed && (isNext || arrival) && expandedSpot !== `__close__${s.id}`);
                      const allArrivalsForSpot = arrivals.filter((a) => a.spot_id === s.id);
                      const assignedNames = (s.assigned_photographers || [])
                        .map((pid) => photographers.find((p) => p.id === pid)?.name)
                        .filter(Boolean);
                      const isAssignedToMe = (s.assigned_photographers || []).includes(viewerPid);
                      const url = mapsUrl(s);
                      return (
                        <div
                          key={s.id}
                          className={`border rounded-lg overflow-hidden transition ${
                            completed
                              ? 'border-slate-200 bg-slate-50 opacity-70'
                              : isNext
                              ? 'border-blue-300 bg-blue-50/50 ring-1 ring-blue-200'
                              : arrival
                              ? 'border-emerald-300 bg-emerald-50/40'
                              : s.is_coverage
                              ? 'border-amber-300 bg-amber-50/30'
                              : 'border-slate-200 bg-white'
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => setExpandedSpot(isExpanded ? `__close__${s.id}` : s.id)}
                            className="w-full text-left px-3 py-2 flex items-start gap-2 hover:bg-slate-50/50"
                          >
                            <span
                              className={`flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-bold shrink-0 mt-0.5 ${
                                completed ? 'bg-slate-400' : isNext ? 'bg-blue-500' : 'bg-emerald-500'
                              }`}
                            >
                              {completed ? '✓' : i + 1}
                            </span>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-2">
                                <h3 className={`font-semibold truncate ${completed ? 'text-slate-500 line-through' : 'text-slate-900'}`}>
                                  {s.name}
                                </h3>
                                <div className="flex items-center gap-1 flex-shrink-0">
                                  {s.is_coverage && (
                                    <Badge className="text-[10px] bg-amber-500 hover:bg-amber-500 text-white">
                                      <RefreshCw className="w-3 h-3 mr-0.5" />
                                      {t('planShare.coverage', { defaultValue: 'Coverage' })}
                                    </Badge>
                                  )}
                                  {isNext && (
                                    <Badge className="text-[10px] bg-blue-500 hover:bg-blue-500 text-white">
                                      {t('planShare.nextUp')}
                                    </Badge>
                                  )}
                                  {arrival && (
                                    <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                                      <CheckCircle2 className="w-3 h-3 mr-0.5" />
                                      {t('planShare.checkedInBadge')}
                                    </Badge>
                                  )}
                                  {completed && (
                                    <Badge className="text-[10px] bg-slate-200 text-slate-600 hover:bg-slate-200">
                                      {t('planShare.done')}
                                    </Badge>
                                  )}
                                  {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                                </div>
                              </div>
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-slate-600 mt-0.5">
                                {(s.arrive_time || s.leave_time) && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3 text-slate-400" />
                                    {s.arrive_time && s.leave_time
                                      ? `${fmtTime(s.arrive_time)} – ${fmtTime(s.leave_time)}`
                                      : fmtTime(s.arrive_time || s.leave_time)}
                                  </span>
                                )}
                                {assignedNames.length > 0 && (
                                  <span className="flex items-center gap-1">
                                    <Camera className="w-3 h-3 text-slate-400" />
                                    {assignedNames.join(', ')}
                                  </span>
                                )}
                              </div>
                            </div>
                          </button>

                          {isExpanded && (
                            <div className="px-3 pb-3 pt-1 border-t border-slate-100 space-y-3">
                              {/* Coverage info banner — explains why this spot is in the photographer's list */}
                              {s.is_coverage && (
                                <div className="bg-amber-50 border border-amber-200 rounded p-2 text-xs">
                                  <p className="text-amber-900 font-medium flex items-center gap-1">
                                    <RefreshCw className="w-3.5 h-3.5" />
                                    {t('planShare.coverageBanner', { defaultValue: 'You are covering this spot' })}
                                  </p>
                                  {s.coverage_for_photographer_id && (
                                    <p className="text-amber-800 mt-0.5">
                                      {t('planShare.coverageFor', { defaultValue: 'For' })}:{' '}
                                      <b>
                                        {photographers.find((p) => p.id === s.coverage_for_photographer_id)?.name
                                          || t('planShare.coverageBreak', { defaultValue: 'a break' })}
                                      </b>
                                    </p>
                                  )}
                                  {s.coverage_note && (
                                    <p className="text-amber-700 italic mt-0.5">{s.coverage_note}</p>
                                  )}
                                </div>
                              )}
                              {/* Location + map link */}
                              {(s.address || (s.lat != null && s.lng != null)) && (
                                <div className="text-xs">
                                  <div className="flex items-start gap-1 text-slate-700">
                                    <MapPin className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
                                    <div className="flex-1 min-w-0">
                                      {s.address && <p className="text-slate-700">{s.address}</p>}
                                      {s.lat != null && s.lng != null && (
                                        <p className="text-slate-400 font-mono text-[11px] mt-0.5">
                                          {s.lat.toFixed(5)}, {s.lng.toFixed(5)}
                                        </p>
                                      )}
                                    </div>
                                  </div>
                                  {url && (
                                    <a
                                      href={url}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex items-center gap-1 mt-1 text-blue-600 hover:underline"
                                    >
                                      <ExternalLink className="w-3 h-3" /> {t('planShare.openInMaps')}
                                    </a>
                                  )}
                                </div>
                              )}

                              {/* Description */}
                              {s.description && (
                                <p className="text-xs text-slate-700 whitespace-pre-wrap leading-relaxed bg-slate-50 p-2 rounded">
                                  {s.description}
                                </p>
                              )}

                              {/* Required gear */}
                              {s.required_gear && s.required_gear.length > 0 && (
                                <div>
                                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                                    <Briefcase className="w-3 h-3" /> {t('planShare.requiredGear')}
                                  </h4>
                                  <div className="flex flex-wrap gap-1">
                                    {s.required_gear.map((g, idx) => (
                                      <Badge key={idx} variant="outline" className="text-[10px] bg-amber-50 text-amber-800 border-amber-200">
                                        {g}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* Reference images */}
                              {s.reference_images && s.reference_images.length > 0 && (
                                <div>
                                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                                    <ImageIcon className="w-3 h-3" /> {t('planShare.referenceImages')} ({s.reference_images.length})
                                  </h4>
                                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-1">
                                    {s.reference_images.map((url, idx) => {
                                      const fullUrl = url.startsWith('http') ? url : `${BACKEND_URL}${url}`;
                                      return (
                                        <a key={idx} href={fullUrl} target="_blank" rel="noreferrer" className="block">
                                          <img
                                            src={fullUrl}
                                            alt=""
                                            className="w-full h-16 object-cover rounded border border-slate-200 hover:opacity-80 transition"
                                          />
                                        </a>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Photographer's own high-res uploads (separate from reference images) */}
                              {viewerPid && isAssignedToMe && (
                                <SpotPhotoUploader
                                  spotId={s.id}
                                  spotName={s.name}
                                  token={token}
                                  session={session}
                                  t={t}
                                />
                              )}

                              {/* Break coverage assignments at this spot */}
                              {(s.break_coverage && s.break_coverage.length > 0) && (
                                <div>
                                  <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
                                    🔁 {t('planShare.breakCoverage')}
                                  </h4>
                                  <div className="space-y-1">
                                    {s.break_coverage.map((c) => {
                                      const coveringName = photographers.find((p) => p.id === c.covering_photographer_id)?.name || '—';
                                      const coversForName = c.covers_for_id ? (photographers.find((p) => p.id === c.covers_for_id)?.name || '—') : null;
                                      const isMe = c.covering_photographer_id === viewerPid;
                                      return (
                                        <div
                                          key={c.id}
                                          className={`text-xs rounded p-2 border ${
                                            isMe ? 'bg-amber-100 border-amber-300' : 'bg-amber-50 border-amber-200'
                                          }`}
                                        >
                                          <p className="text-amber-900">
                                            <b>{isMe ? t('planShare.youCover') : coveringName}</b>
                                            {coversForName ? <> · {t('planShare.coverFor')} <b>{coversForName}</b></> : null}
                                            <span className="text-amber-700"> · {c.start_time}–{c.end_time}</span>
                                          </p>
                                          {c.note && <p className="text-[11px] text-amber-700 mt-0.5">{c.note}</p>}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              )}

                              {/* Other arrivals (team awareness) */}
                              {allArrivalsForSpot.length > 0 && (
                                <div className="text-[11px] text-slate-500">
                                  {allArrivalsForSpot.map((a) => (
                                    <p key={a.id} className="flex items-center gap-1">
                                      <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                                      {a.photographer_name || '—'}
                                      {a.arrived_at && <span> · {shortTime(a.arrived_at)}</span>}
                                      {a.departed_at && <span className="text-slate-400"> → {shortTime(a.departed_at)}</span>}
                                    </p>
                                  ))}
                                </div>
                              )}

                              {/* Action buttons */}
                              {viewerPid && isAssignedToMe && (
                                <div className="flex gap-2 pt-1">
                                  {arrival ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      disabled={busySpot === s.id}
                                      onClick={() => checkOutSpot(s)}
                                      className="flex-1 border-amber-300 text-amber-700 hover:bg-amber-50"
                                    >
                                      <LogOut className="w-3.5 h-3.5 mr-1" />
                                      {busySpot === s.id ? '…' : t('planShare.checkOut')}
                                    </Button>
                                  ) : (
                                    <Button
                                      type="button"
                                      size="sm"
                                      disabled={busySpot === s.id}
                                      onClick={() => checkInSpot(s)}
                                      className="flex-1 bg-emerald-600 hover:bg-emerald-700"
                                    >
                                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                                      {busySpot === s.id ? '…' : t('planShare.checkIn')}
                                    </Button>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                </div>
              )}
                  </>
                );
              })()}
            </CardContent>
          </Card>
        </div>

        {/* Map column */}
        <div className={`md:col-span-5 ${activeTab === 'map' ? 'block' : 'hidden md:block'}`}>
          <Card className="md:sticky md:top-20">
            <CardContent className="p-2">
              <div className="rounded-xl overflow-hidden" style={{ height: 480 }}>
                <GoogleMap
                  mapContainerStyle={{ width: '100%', height: '100%' }}
                  center={mapCenter}
                  zoom={spotsWithCoords.length === 1 ? 15 : 13}
                  options={{
                    disableDefaultUI: true,
                    zoomControl: true,
                    streetViewControl: false,
                    fullscreenControl: false,
                  }}
                >
                  {courseTracks
                    .filter((ct) => !ct.is_floor_plan && Array.isArray(ct.track_points) && ct.track_points.length > 1
                      && Number.isFinite(ct.track_points[0]?.lat))
                    .map((ct) => (
                      <Polyline
                        key={ct.id}
                        path={ct.track_points.map((p) => ({ lat: p.lat, lng: p.lng }))}
                        options={{ strokeColor: ct.color || '#0ea5e9', strokeWeight: 3, strokeOpacity: 0.7 }}
                      />
                    ))}
                  {spotsWithCoords.map((s, i) => (
                    <Marker
                      key={s.id}
                      position={{ lat: s.lat, lng: s.lng }}
                      label={{ text: String(i + 1), color: '#fff', fontWeight: 'bold', fontSize: '12px' }}
                    />
                  ))}
                  {tracking.live.map((loc) => {
                    const color = colorForPhotographer(loc.photographer_id, photographers);
                    return (
                      <Marker
                        key={loc.id || `${loc.photographer_id}-live`}
                        position={{ lat: loc.lat, lng: loc.lng }}
                        icon={{
                          path: window.google?.maps?.SymbolPath?.CIRCLE,
                          scale: 8,
                          fillColor: color,
                          fillOpacity: 0.9,
                          strokeColor: '#fff',
                          strokeWeight: 2,
                        }}
                      />
                    );
                  })}
                  {arrivals.filter((a) => Number.isFinite(a.lat)).map((a) => (
                    <Marker
                      key={a.id || `${a.spot_id}-${a.photographer_id}`}
                      position={{ lat: a.lat, lng: a.lng }}
                      icon={{
                        path: window.google?.maps?.SymbolPath?.BACKWARD_CLOSED_ARROW,
                        scale: 5,
                        fillColor: a.departed_at ? '#94a3b8' : '#10b981',
                        fillOpacity: 1,
                        strokeColor: '#fff',
                        strokeWeight: 1,
                      }}
                    />
                  ))}
                </GoogleMap>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Floating compact chat */}
      {planData.allow_chat !== false && (
        <div className="fixed bottom-3 right-3 z-30 w-[min(360px,calc(100vw-1.5rem))]">
          <Card className="shadow-lg overflow-hidden">
            <button
              type="button"
              onClick={() => setChatOpen((v) => !v)}
              className="w-full px-3 py-2 bg-slate-800 text-white text-sm flex items-center justify-between"
            >
              <span className="flex items-center gap-2">
                <MessageCircle className="w-4 h-4" />
                {t('planShare.chatPanel')}
                {messages.length > 0 && (
                  <Badge className="bg-emerald-500 hover:bg-emerald-500 text-[10px]">{messages.length}</Badge>
                )}
              </span>
              {chatOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronUp className="w-4 h-4" />}
            </button>
            {chatOpen && (
              <div className="bg-white">
                <div
                  ref={messagesScrollerRef}
                  className="h-64 overflow-y-auto p-2 space-y-2 bg-slate-50/40"
                  style={{ overscrollBehavior: 'contain', WebkitOverflowScrolling: 'touch', touchAction: 'pan-y' }}
                  onTouchMove={(e) => e.stopPropagation()}
                  onWheel={(e) => e.stopPropagation()}
                >
                  {messages.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-6">{t('planShare.chatEmpty')}</p>
                  ) : (
                    messages.map((m) => {
                      const isSelf = m.sender_id && m.sender_id === viewerPid;
                      const dotColor = colorForPhotographer(m.sender_id, photographers);
                      return (
                        <div key={m.id} className={`flex ${isSelf ? 'justify-end' : 'justify-start'}`}>
                          <div
                            className={`max-w-[85%] rounded-xl px-2.5 py-1.5 text-xs ${
                              isSelf
                                ? 'bg-emerald-500 text-white'
                                : 'bg-white border border-slate-200 text-slate-800'
                            }`}
                          >
                            {!isSelf && (
                              <p className="text-[10px] font-semibold opacity-80 mb-0.5 flex items-center gap-1">
                                {m.sender_id && (
                                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: dotColor }} />
                                )}
                                {m.sender_name || t('planShare.guest')}
                              </p>
                            )}
                            {m.text && <p className="whitespace-pre-wrap break-words">{m.text}</p>}
                            {m.image_url && (
                              <a href={`${BACKEND_URL}${m.image_url}`} target="_blank" rel="noreferrer">
                                <img
                                  src={`${BACKEND_URL}${m.image_url}`}
                                  alt=""
                                  className="mt-1 max-h-32 rounded-md border border-slate-200"
                                />
                              </a>
                            )}
                            {m.location && (
                              <a
                                href={`https://www.google.com/maps/search/?api=1&query=${m.location.lat},${m.location.lng}`}
                                target="_blank"
                                rel="noreferrer"
                                className={`text-[11px] mt-1 inline-flex items-center gap-1 underline ${
                                  isSelf ? 'text-white' : 'text-blue-600'
                                }`}
                              >
                                <MapPin className="w-3 h-3" />
                                {m.location.label || t('planShare.shareLocation')}
                              </a>
                            )}
                            <p className={`text-[9px] mt-0.5 ${isSelf ? 'text-emerald-50' : 'text-slate-400'}`}>
                              {shortTime(m.created_at)}
                            </p>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
                <form onSubmit={onSendForm} className="p-1.5 border-t border-slate-200 flex items-center gap-1">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    className="hidden"
                  />
                  <Button type="button" size="sm" variant="ghost" onClick={handleAttachClick} title={t('planShare.attachImage')} className="px-2">
                    <ImageIcon className="w-4 h-4" />
                  </Button>
                  <Button type="button" size="sm" variant="ghost" onClick={handleShareLocation} title={t('planShare.shareLocation')} className="px-2">
                    <MapPin className="w-4 h-4" />
                  </Button>
                  <input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t('planShare.messagePlaceholder')}
                    maxLength={4000}
                    className="flex-1 h-8 px-2 rounded-lg border border-slate-200 focus:border-emerald-500 outline-none text-xs"
                  />
                  <Button type="submit" size="sm" disabled={sending || !text.trim()} className="bg-emerald-600 hover:bg-emerald-700 px-2">
                    <Send className="w-4 h-4" />
                  </Button>
                </form>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}

// Per-spot photographer-side high-res photo upload + gallery with per-photo
// notes. Selecting files stages them into a list where each gets its own
// note input before submit.
function SpotPhotoUploader({ spotId, spotName, token, session, t }) {
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState([]); // [{ file, preview, note }]
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editingNoteText, setEditingNoteText] = useState('');
  const inputRef = useRef(null);

  const refresh = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/share/${token}/spots/${spotId}/photos`, {
        headers: buildAuthHeader(session),
      });
      setPhotos(r.data || []);
    } catch {
      /* swallow */
    }
  }, [token, spotId, session]);

  useEffect(() => { refresh(); }, [refresh]);

  // Cleanup object URLs when pending list changes/unmounts.
  useEffect(() => () => {
    pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
  }, [pending]);

  const onFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setPending((prev) => [
      ...prev,
      ...files.map((file) => ({
        id: `${file.name}-${file.size}-${Math.random()}`,
        file,
        preview: URL.createObjectURL(file),
        note: '',
      })),
    ]);
    e.target.value = '';
  };

  const updatePendingNote = (id, note) => {
    setPending((prev) => prev.map((p) => (p.id === id ? { ...p, note } : p)));
  };

  const removePending = (id) => {
    setPending((prev) => {
      const target = prev.find((p) => p.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((p) => p.id !== id);
    });
  };

  const submitPending = async () => {
    if (!pending.length || busy) return;
    setBusy(true);
    let ok = 0;
    for (const item of pending) {
      const form = new FormData();
      form.append('file', item.file);
      if (item.note) form.append('note', item.note);
      try {
        await axios.post(
          `${API}/share/${token}/spots/${spotId}/photos`,
          form,
          { headers: { ...buildAuthHeader(session), 'Content-Type': 'multipart/form-data' } },
        );
        ok += 1;
      } catch (err) {
        toast.error(err.response?.data?.detail || t('planShare.uploadFailed'));
      }
    }
    if (ok) {
      toast.success(t('planShare.photosUploaded', { count: ok, spot: spotName }));
      pending.forEach((p) => p.preview && URL.revokeObjectURL(p.preview));
      setPending([]);
      refresh();
    }
    setBusy(false);
  };

  const startEditNote = (photo) => {
    setEditingNoteId(photo.id);
    setEditingNoteText(photo.note || '');
  };
  const saveNote = async (photo) => {
    try {
      const r = await axios.patch(
        `${API}/share/${token}/spot-photos/${photo.id}`,
        { note: editingNoteText },
        { headers: buildAuthHeader(session) },
      );
      setPhotos((prev) => prev.map((p) => (p.id === photo.id ? { ...p, note: r.data.note } : p)));
      setEditingNoteId(null);
      setEditingNoteText('');
      toast.success(t('planShare.noteSaved', { defaultValue: 'Note saved' }));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('planShare.uploadFailed'));
    }
  };

  const fmtBytes = (n) => {
    if (!n) return '';
    if (n < 1024) return `${n}B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)}KB`;
    return `${(n / 1024 / 1024).toFixed(1)}MB`;
  };

  return (
    <div className="border-t border-slate-100 pt-2">
      <h4 className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide mb-1 flex items-center gap-1">
        <ImageIcon className="w-3 h-3" /> {t('planShare.myPhotos')} ({photos.length})
      </h4>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={onFiles}
        className="hidden"
      />
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
        className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-50"
      >
        <ImageIcon className="w-3.5 h-3.5 mr-1" />
        {t('planShare.pickPhotos', { defaultValue: 'Pick photos' })}
      </Button>
      <p className="text-[10px] text-slate-400 mt-1">{t('planShare.uploadHiResHint')}</p>

      {/* Staging area: pending files, one row each with note + remove. */}
      {pending.length > 0 && (
        <div className="mt-2 space-y-2 bg-slate-50 border border-slate-200 rounded p-2">
          <p className="text-[11px] text-slate-600 font-medium">
            {t('planShare.pendingPhotos', { defaultValue: 'Ready to upload', count: pending.length })}: {pending.length}
          </p>
          {pending.map((p) => (
            <div key={p.id} className="flex items-start gap-2 bg-white border rounded p-1.5">
              <img src={p.preview} alt="" className="w-12 h-12 object-cover rounded flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] truncate text-slate-700">{p.file.name}</p>
                <p className="text-[10px] text-slate-400">{fmtBytes(p.file.size)}</p>
                <textarea
                  value={p.note}
                  onChange={(e) => updatePendingNote(p.id, e.target.value)}
                  rows={2}
                  placeholder={t('planShare.notePlaceholder', { defaultValue: 'Note for editor (optional)' })}
                  className="w-full mt-1 text-[11px] border rounded px-1.5 py-1 resize-none"
                />
              </div>
              <button
                type="button"
                onClick={() => removePending(p.id)}
                className="text-red-500 hover:text-red-700 text-xs px-1"
              >
                ✕
              </button>
            </div>
          ))}
          <Button
            type="button"
            size="sm"
            disabled={busy}
            onClick={submitPending}
            className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {busy
              ? t('planShare.uploading')
              : t('planShare.uploadCount', { defaultValue: 'Upload {{count}} photos', count: pending.length })}
          </Button>
        </div>
      )}

      {/* Existing uploads, with note display + inline edit. */}
      {photos.length > 0 && (
        <div className="mt-2 space-y-1">
          {photos.map((p) => {
            const fullUrl = p.url.startsWith('http') ? p.url : `${BACKEND_URL}${p.url}`;
            return (
              <div key={p.id} className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded p-1.5">
                <a href={fullUrl} target="_blank" rel="noreferrer" className="flex-shrink-0">
                  <img src={fullUrl} alt="" className="w-12 h-12 object-cover rounded" />
                </a>
                <div className="flex-1 min-w-0">
                  <p className="text-[11px] truncate text-slate-700">{p.filename || '—'}</p>
                  <p className="text-[10px] text-slate-400">{fmtBytes(p.size_bytes)}</p>
                  {editingNoteId === p.id ? (
                    <div className="flex items-start gap-1 mt-1">
                      <textarea
                        value={editingNoteText}
                        onChange={(e) => setEditingNoteText(e.target.value)}
                        rows={2}
                        className="flex-1 text-[11px] border rounded px-1.5 py-1 resize-none"
                        autoFocus
                      />
                      <div className="flex flex-col gap-1">
                        <button type="button" onClick={() => saveNote(p)} className="text-[10px] bg-emerald-600 text-white px-1.5 py-0.5 rounded">✓</button>
                        <button type="button" onClick={() => setEditingNoteId(null)} className="text-[10px] bg-slate-300 text-slate-700 px-1.5 py-0.5 rounded">✕</button>
                      </div>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditNote(p)}
                      className="block w-full text-left mt-1 text-[10px] text-slate-600 italic hover:text-slate-900"
                    >
                      {p.note || t('planShare.addNote', { defaultValue: '+ Add note' })}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
